import { useTreatmentPlanContext } from 'app/containers/DentalTreatmentManagement/TreatmentPlan/context'
import { useMemo } from 'react'
import { ToothStatusEnum } from 'app/containers/DentalTreatmentManagement/TreatmentPlan/type'
import {
  SvgRemainingToothRootsDisplayIcon,
  SvgTeethWithPathologyDisplayIcon,
  SvgToothLossDisplayIcon,
  SvgToothWithIndicationForExtractionDisplayIcon,
} from 'app/assets/svg-assets'
import { isEqual } from 'lodash'
import { IPropsToothAction } from './type'

export const useHook = ({ item, isUpTooth, index }: IPropsToothAction) => {
  const {
    bridgeToothBottomTabResult,
    bridgeToothTopTabResult,
    onChangeStatusToothSizeAndMorphologyTabResult,
    bridgeBottomTabResultSelecting,
    onAddBridgeBottomTabResult,
    onChangeBridgeBottomTabResult,
    onDeleteBridgeBottomTabResult,
    onChangeBridgeTopTabResult,
    onAddBridgeTopTabResult,
    bridgeTopTabResultSelecting,
    onDeleteBridgeTopTabResult,
  } = useTreatmentPlanContext()

  const currentBridgeTooth = useMemo(() => {
    if (isUpTooth) {
      return bridgeToothTopTabResult?.find(
        itm => isEqual(itm?.indexStart, index) || isEqual(index, itm?.indexEnd),
      )
    }

    return bridgeToothBottomTabResult?.find(
      itm => isEqual(itm?.indexStart, index) || isEqual(index, itm?.indexEnd),
    )
  }, [bridgeToothBottomTabResult, isUpTooth, index, bridgeToothTopTabResult])

  const isDisableAction = useMemo(() => {
    if (isUpTooth) {
      if (bridgeBottomTabResultSelecting) return true

      if (bridgeTopTabResultSelecting) {
        const listTopTabResultToothBridge =
          bridgeToothTopTabResult?.filter(
            itm => itm?.indexStart !== undefined && itm?.indexEnd !== undefined,
          ) ?? []

        if (!listTopTabResultToothBridge?.length) return false

        const nearBridgeRight = listTopTabResultToothBridge?.find(
          itm =>
            itm?.indexStart !== undefined &&
            itm?.indexEnd !== undefined &&
            (itm?.indexStart ?? 0) >
              (bridgeTopTabResultSelecting?.indexStart ?? 0),
        )

        if (nearBridgeRight && index >= (nearBridgeRight?.indexEnd ?? 0)) {
          return true
        }

        const nearBridgeLeft = listTopTabResultToothBridge?.find(
          itm =>
            (itm?.indexEnd ?? 0) <
            (bridgeTopTabResultSelecting?.indexStart ?? 0),
        )

        if (nearBridgeLeft && index <= (nearBridgeLeft?.indexStart ?? 0)) {
          return true
        }
      }

      return !!bridgeToothTopTabResult?.find(
        itm => (itm?.indexStart ?? 0) < index && index < (itm?.indexEnd ?? 0),
      )
    }

    if (bridgeTopTabResultSelecting) return true

    if (bridgeBottomTabResultSelecting) {
      const listBottomTabResultToothBridge =
        bridgeToothBottomTabResult?.filter(
          itm => itm?.indexStart !== undefined && itm?.indexEnd !== undefined,
        ) ?? []

      if (!listBottomTabResultToothBridge?.length) return false

      const nearBridgeRight = listBottomTabResultToothBridge?.find(
        itm =>
          itm?.indexStart !== undefined &&
          itm?.indexEnd !== undefined &&
          (itm?.indexStart ?? 0) >
            (bridgeBottomTabResultSelecting?.indexStart ?? 0),
      )

      if (nearBridgeRight && index >= (nearBridgeRight?.indexEnd ?? 0)) {
        return true
      }

      const nearBridgeLeft = listBottomTabResultToothBridge?.find(
        itm =>
          (itm?.indexEnd ?? 0) <
          (bridgeBottomTabResultSelecting?.indexStart ?? 0),
      )

      if (nearBridgeLeft && index <= (nearBridgeLeft?.indexStart ?? 0)) {
        return true
      }
    }

    return !!bridgeToothBottomTabResult?.find(
      itm => (itm?.indexStart ?? 0) < index && index < (itm?.indexEnd ?? 0),
    )
  }, [
    bridgeToothBottomTabResult,
    bridgeTopTabResultSelecting,
    bridgeBottomTabResultSelecting,
    index,
    bridgeToothTopTabResult,
  ])

  const opacityImage = useMemo(() => {
    if (isDisableAction) return 0.3

    if (isEqual(item?.currentStatus, ToothStatusEnum.toothLoss)) {
      return 0.3
    }
    return 1
  }, [item?.currentStatus, isDisableAction])

  const iconDefine = useMemo(() => {
    switch (item?.currentStatus) {
      case ToothStatusEnum.toothLoss:
        return <SvgToothLossDisplayIcon />

      case ToothStatusEnum.teethWithPathology:
        return <SvgTeethWithPathologyDisplayIcon />

      case ToothStatusEnum.toothWithIndicationForExtraction:
        return <SvgToothWithIndicationForExtractionDisplayIcon />

      case ToothStatusEnum.remainingToothRoots:
        return <SvgRemainingToothRootsDisplayIcon />

      default:
        return null
    }
  }, [
    item?.currentStatus,
    bridgeTopTabResultSelecting,
    bridgeBottomTabResultSelecting,
  ])

  const urlImage = useMemo(() => {
    if (currentBridgeTooth) {
      return item?.imageToothImplant
    }

    switch (item?.currentStatus) {
      case ToothStatusEnum.implantPlacement:
        return item?.imageToothImplant
      default:
        return item?.imageTooth
    }
  }, [item, currentBridgeTooth, index])

  const _onChangeTeethWithPathology = () => {
    if (!item) return

    onChangeStatusToothSizeAndMorphologyTabResult?.(
      item?.position,
      item.key,
      ToothStatusEnum.teethWithPathology,
    )

    _onDeleteBridgeTooth()
  }

  const _onChangeToothLoss = () => {
    if (!item) return
    onChangeStatusToothSizeAndMorphologyTabResult?.(
      item?.position,
      item.key,
      ToothStatusEnum.toothLoss,
    )

    _onDeleteBridgeTooth()
  }

  const _onChangeImplantPlacement = () => {
    if (!item) return

    onChangeStatusToothSizeAndMorphologyTabResult?.(
      item?.position,
      item.key,
      ToothStatusEnum.implantPlacement,
    )

    _onDeleteBridgeTooth()
  }

  const _onChangeRemainingToothRoots = () => {
    if (!item) return

    onChangeStatusToothSizeAndMorphologyTabResult?.(
      item?.position,
      item.key,
      ToothStatusEnum.remainingToothRoots,
    )

    _onDeleteBridgeTooth()
  }

  const _onChangeToothWithIndicationForExtraction = () => {
    if (!item) return

    onChangeStatusToothSizeAndMorphologyTabResult?.(
      item?.position,
      item.key,
      ToothStatusEnum.toothWithIndicationForExtraction,
    )

    _onDeleteBridgeTooth()
  }

  const _onDeleteBridgeTooth = () => {
    if (isUpTooth) {
      const itemExist = bridgeToothTopTabResult?.filter(
        itm => isEqual(itm?.indexEnd, index) || isEqual(itm?.indexStart, index),
      )

      if (itemExist?.length) {
        onDeleteBridgeTopTabResult?.(itemExist?.map(itm => itm?.id ?? ''))
      }
    }

    if (!isUpTooth) {
      const itemExist = bridgeToothBottomTabResult?.filter(
        itm => isEqual(itm?.indexEnd, index) || isEqual(itm?.indexStart, index),
      )

      if (itemExist?.length) {
        onDeleteBridgeBottomTabResult?.(itemExist?.map(itm => itm?.id ?? ''))
      }
    }
  }

  const _onDeleteStatusTooth = () => {
    if (!item) return
    onChangeStatusToothSizeAndMorphologyTabResult?.(
      item?.position,
      item.key,
      undefined,
    )

    _onDeleteBridgeTooth()
  }

  const _onAddBridge = () => {
    if (!item) return
    if (isUpTooth) {
      onAddBridgeTopTabResult?.(item?.key, index)
      return
    }

    onAddBridgeBottomTabResult?.(item?.key, index)
  }

  const _onChangeBridge = () => {
    if (!item) return
    if (isUpTooth) {
      onChangeBridgeTopTabResult?.(
        bridgeTopTabResultSelecting?.id,
        item?.key,
        index,
      )
      return
    }

    onChangeBridgeBottomTabResult?.(
      bridgeBottomTabResultSelecting?.id,
      item?.key,
      index,
    )
  }

  return {
    _onDeleteStatusTooth,
    _onChangeImplantPlacement,
    _onChangeRemainingToothRoots,
    _onChangeTeethWithPathology,
    _onChangeToothLoss,
    _onChangeToothWithIndicationForExtraction,
    urlImage,
    iconDefine,
    opacityImage,
    isDisableAction,
    _onAddBridge,
    bridgeBottomTabResultSelecting,
    bridgeTopTabResultSelecting,
    _onChangeBridge,
    currentBridgeTooth,
  }
}
